import { Input, SimpleGrid } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { useAppToast } from '../../helpers'
import { EmployeeService } from '../../services/Employee/EmployeeService'
import { IEvaluationEmployeeRaw } from '../../services/Employee/types'
import { ISector, SectorService } from '../../services/Sector/SectorService'
import { DetailField } from '../DetailField'
import { FormField } from '../FormField'
import { FormSelect, SearchableSelect } from '../Select'
import {
  IEvaluatedEmployeeForm,
  ORGANIZATIONAL_LEVEL_OPTIONS,
  organizationalLevelLabel,
  TEvaluatedEmployeeErrors
} from './evaluatedEmployeeForm'

const NO_EMPLOYEES = 'Nenhum colaborador ativo neste setor. Fale com o RH para cadastrá-lo.'
const LOAD_FAILED = 'Não foi possível carregar os colaboradores. Troque o setor para tentar de novo.'

interface IEvaluatedEmployeeFieldsProps {
  value: IEvaluatedEmployeeForm
  onChange: (patch: Partial<IEvaluatedEmployeeForm>) => void
  errors: TEvaluatedEmployeeErrors
  /** Acelera and Conexão also ask the nickname (filled from the record) and the birthdate. */
  asksPersonal: boolean
}

/**
 * Who a Magenta evaluates (ABRA-320): the sector, then an active collaborator of it, whose data
 * shows locked for the manager to check. The evaluation keeps a copy of it.
 */
export function EvaluatedEmployeeFields({ value, onChange, errors, asksPersonal }: IEvaluatedEmployeeFieldsProps) {
  const toast = useAppToast()
  const [sectors, setSectors] = useState<ISector[] | null>(null)
  const [employees, setEmployees] = useState<IEvaluationEmployeeRaw[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    new SectorService()
      .listForSelect()
      .then(setSectors)
      .catch((error) => {
        setSectors([])
        toast.error('Erro ao carregar os setores', error)
      })
  }, [])

  useEffect(() => {
    setEmployees(null)
    setLoadFailed(false)
    if (!value.sectorId) return
    // A previous sector's answer that arrives late is dropped, so the list is always this sector's.
    let active = true
    new EmployeeService()
      .listForEvaluations(Number(value.sectorId))
      .then((list) => {
        if (active) setEmployees(list)
      })
      .catch((error) => {
        if (!active) return
        // Out of loading, so the field says what happened; another sector tries again.
        setEmployees([])
        setLoadFailed(true)
        toast.error('Erro ao carregar os colaboradores', error)
      })
    return () => {
      active = false
    }
  }, [value.sectorId])

  const employee = value.employee
  const noEmployees = employees?.length === 0 && !loadFailed
  const pick = (id: number | '') => {
    const picked = employees?.find((item) => item.id === id) ?? null
    onChange({ employee: picked, nickname: picked?.nickname ?? '', organizationalLevel: '' })
  }

  return (
    <>
      <FormField label="Setor" error={errors.sector_id} isRequired>
        <FormSelect
          value={value.sectorId}
          onChange={(sectorId) => onChange({ sectorId, employee: null, nickname: '', organizationalLevel: '' })}
          options={(sectors ?? []).map((sector) => ({ value: sector.id, label: sector.name }))}
          isLoading={sectors === null}
        />
      </FormField>

      <FormField
        label="Colaborador(a)"
        error={errors.employee_id ?? (loadFailed ? [LOAD_FAILED] : noEmployees ? [NO_EMPLOYEES] : undefined)}
        isRequired
      >
        <SearchableSelect<number>
          options={(employees ?? []).map((item) => ({
            value: item.id,
            label: item.position ? `${item.name} · ${item.position}` : item.name
          }))}
          value={employee?.id ?? ''}
          onChange={pick}
          placeholder={value.sectorId ? 'Selecione o colaborador' : 'Selecione o setor primeiro'}
          entity="colaborador"
          emptyMessage={loadFailed ? LOAD_FAILED : NO_EMPLOYEES}
          isDisabled={!value.sectorId}
          isLoading={!!value.sectorId && employees === null}
        />
      </FormField>

      {employee && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
          <DetailField label="Nome" value={employee.name} />
          <DetailField label="E-mail" value={employee.email} />
          <DetailField label="Cargo" value={employee.position} />
          {employee.organizational_level && (
            <DetailField label="Nível organizacional" value={organizationalLevelLabel(employee.organizational_level)} />
          )}
        </SimpleGrid>
      )}

      {employee && !employee.organizational_level && (
        <FormField
          label="Nível organizacional do cargo"
          error={errors.organizational_level}
          helperText="O cadastro deste colaborador não tem nível. O escolhido aqui vale só para esta avaliação."
          isRequired
        >
          <FormSelect
            value={value.organizationalLevel}
            onChange={(organizationalLevel) => onChange({ organizationalLevel })}
            options={ORGANIZATIONAL_LEVEL_OPTIONS}
          />
        </FormField>
      )}

      {asksPersonal && (
        <>
          <FormField label="Como gosta de ser chamado(a)?" error={errors.nickname} isRequired>
            <Input value={value.nickname} onChange={(event) => onChange({ nickname: event.target.value })} />
          </FormField>
          <FormField label="Data de nascimento" error={errors.birthdate} isRequired>
            <Input
              type="date"
              value={value.birthdate}
              onChange={(event) => onChange({ birthdate: event.target.value })}
            />
          </FormField>
        </>
      )}
    </>
  )
}
